// Application-wide time zone helper.
// Reads the admin-selected zone from localStorage ('app.timezone').
// Falls back to the browser's detected zone.

const STORAGE_KEY = 'app.timezone';

export function getAppTimezone() {
  try {
    const tz = localStorage.getItem(STORAGE_KEY);
    if (tz) return tz;
  } catch (_) {}
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
}

export function setAppTimezone(tz) {
  try {
    if (tz) localStorage.setItem(STORAGE_KEY, tz);
    else    localStorage.removeItem(STORAGE_KEY);
    // Broadcast so any subscribed UI can re-render
    window.dispatchEvent(new CustomEvent('app:timezone-changed', { detail: tz }));
  } catch (_) {}
}

// Format any date/iso string in the configured timezone.
// Accepts the same `options` object as `Date.prototype.toLocaleString`.
export function formatInTz(value, options = {}, locale = 'en-GB') {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString(locale, { timeZone: getAppTimezone(), ...options });
  } catch (_) {
    return d.toLocaleString(locale, options);
  }
}

// Curated list of commonly-used IANA timezones for the dropdown.
// (Supplemented at runtime by Intl.supportedValuesOf when available.)
export const COMMON_TIMEZONES = [
  { value: 'UTC',                          label: '(GMT+00:00) UTC' },
  { value: 'Pacific/Honolulu',             label: '(GMT-10:00) Hawaii' },
  { value: 'America/Anchorage',            label: '(GMT-09:00) Alaska' },
  { value: 'America/Los_Angeles',          label: '(GMT-08:00) Pacific Time (US & Canada)' },
  { value: 'America/Denver',               label: '(GMT-07:00) Mountain Time (US & Canada)' },
  { value: 'America/Chicago',              label: '(GMT-06:00) Central Time (US & Canada)' },
  { value: 'America/New_York',             label: '(GMT-05:00) Eastern Time (US & Canada)' },
  { value: 'America/Halifax',              label: '(GMT-04:00) Atlantic Time (Canada)' },
  { value: 'America/Sao_Paulo',            label: '(GMT-03:00) Brasilia' },
  { value: 'Atlantic/Azores',              label: '(GMT-01:00) Azores' },
  { value: 'Europe/London',                label: '(GMT+00:00) London, Dublin, Edinburgh' },
  { value: 'Europe/Paris',                 label: '(GMT+01:00) Paris, Berlin, Madrid, Rome' },
  { value: 'Europe/Athens',                label: '(GMT+02:00) Athens, Istanbul, Helsinki' },
  { value: 'Africa/Cairo',                 label: '(GMT+02:00) Cairo' },
  { value: 'Europe/Moscow',                label: '(GMT+03:00) Moscow, St. Petersburg' },
  { value: 'Asia/Dubai',                   label: '(GMT+04:00) Dubai, Abu Dhabi, Muscat' },
  { value: 'Asia/Karachi',                 label: '(GMT+05:00) Karachi, Islamabad' },
  { value: 'Asia/Kolkata',                 label: '(GMT+05:30) India Standard Time (Chennai, Mumbai, Delhi)' },
  { value: 'Asia/Kathmandu',               label: '(GMT+05:45) Kathmandu' },
  { value: 'Asia/Dhaka',                   label: '(GMT+06:00) Dhaka' },
  { value: 'Asia/Bangkok',                 label: '(GMT+07:00) Bangkok, Hanoi, Jakarta' },
  { value: 'Asia/Singapore',               label: '(GMT+08:00) Singapore, Kuala Lumpur' },
  { value: 'Asia/Hong_Kong',               label: '(GMT+08:00) Hong Kong' },
  { value: 'Asia/Shanghai',                label: '(GMT+08:00) Beijing, Shanghai' },
  { value: 'Asia/Taipei',                  label: '(GMT+08:00) Taipei' },
  { value: 'Australia/Perth',              label: '(GMT+08:00) Perth' },
  { value: 'Asia/Tokyo',                   label: '(GMT+09:00) Tokyo, Osaka, Seoul' },
  { value: 'Australia/Adelaide',           label: '(GMT+09:30) Adelaide, Darwin' },
  { value: 'Australia/Sydney',             label: '(GMT+10:00) Sydney, Melbourne, Brisbane' },
  { value: 'Pacific/Guam',                 label: '(GMT+10:00) Guam' },
  { value: 'Pacific/Noumea',               label: '(GMT+11:00) Noumea' },
  { value: 'Pacific/Auckland',             label: '(GMT+12:00) Auckland, Wellington' },
];

// Return either the full IANA list (modern browsers) or the curated subset.
export function listAllTimezones() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      const all = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(all) && all.length > 0) return all;
    }
  } catch (_) {}
  return COMMON_TIMEZONES.map(t => t.value);
}
